import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import leftstar from './left-star.png';
import './landing.css';

function Details() {
  const [darkMode] = useState(true); // Default to dark mode
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeBlockId, setActiveBlockId] = useState(null); // Track which block should show the tooltip
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [contactInfo, setContactInfo] = useState('');
  const [contactMethod, setContactMethod] = useState('email'); // 'email' or 'telegram'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const cashRef = useRef(null);
  const bondRef = useRef(null);
  const modalRef = useRef(null);

  // SheetDB endpoint
  const SHEETDB_ENDPOINT = process.env.REACT_APP_SHEETDB_API_URL;

  // Apply dark mode to entire document including html/body
  useEffect(() => {
    const applyDarkMode = () => {
      if (darkMode) {
        document.documentElement.classList.add('dark-mode');
        document.body.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
        document.body.classList.remove('dark-mode');
      }
    };
    
    // Apply immediately but also after a very short delay to ensure all transitions start together
    applyDarkMode();
    const timer = setTimeout(applyDarkMode, 10);
    
    return () => clearTimeout(timer);
  }, [darkMode]);

  // Effect to handle scrolling to the expanded section
  useEffect(() => {
    if (expandedSection) {
      const targetRef = expandedSection === 'cash' ? cashRef : bondRef;
      if (targetRef.current) {
        // Give time for the expansion animation to start
        setTimeout(() => {
          targetRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }, 100);
      }
    }
  }, [expandedSection]);

  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const toggleTooltip = (token, blockId) => {
    if (activeTooltip === token && activeBlockId === blockId) {
      setActiveTooltip(null);
      setActiveBlockId(null);
    } else {
      setActiveTooltip(token);
      setActiveBlockId(blockId);
    }
  };

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setShowEmailModal(false);
      setContactInfo('');
      setSubmitStatus(null);
    }
    // Collapse the focused section when clicking on blank space
    if (expandedSection && !e.target.closest('.section')) {
      setExpandedSection(null);
    }
    setActiveTooltip(null);
    setActiveBlockId(null);
  };

  const handleUpdatesClick = (e) => {
    e.preventDefault();
    setShowEmailModal(true);
  };

  const handleContactMethodChange = (method) => {
    setContactMethod(method);
    setContactInfo('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Get entered info
      let cleanedContactInfo = contactInfo.trim();
      
      // For Telegram: remove @ symbol if present
      if (contactMethod === 'telegram' && cleanedContactInfo.startsWith('@')) {
        cleanedContactInfo = cleanedContactInfo.substring(1);
        console.log('Removed @ symbol from Telegram handle:', cleanedContactInfo);
      }
      
      // Log the information (this will be visible in the console)
      console.log('CONTACT INFO SAVED:', {
        contact_type: contactMethod,
        contact_info: cleanedContactInfo,
        timestamp: new Date().toISOString()
      });
      
      // Store locally in localStorage as a backup
      try {
        const savedContacts = JSON.parse(localStorage.getItem('savedContacts') || '[]');
        savedContacts.push({
          contact_type: contactMethod,
          contact_info: cleanedContactInfo,
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('savedContacts', JSON.stringify(savedContacts));
        console.log('Saved to localStorage successfully');
      } catch (storageErr) {
        console.error('Error saving to localStorage:', storageErr);
      }
      
      // Send to SheetDB as well (but don't let failures affect the user experience)
      try {
        console.log('Attempting to send to SheetDB:', SHEETDB_ENDPOINT);
        
        // Use a simple format that SheetDB expects
        const sheetPayload = {
          contact_type: contactMethod,
          contact_info: cleanedContactInfo,
          timestamp: new Date().toISOString()
        };
        
        console.log('SheetDB payload:', sheetPayload);
        
        // Try a direct fetch with minimal options
        fetch(SHEETDB_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: [sheetPayload] })
        })
        .then(async response => {
          console.log('SheetDB response status:', response.status);
          console.log('SheetDB response headers:', Object.fromEntries([...response.headers.entries()]));
          
          // Try to read response body regardless of status code
          let responseBody;
          try {
            const textResponse = await response.text();
            console.log('SheetDB raw response:', textResponse);
            try {
              responseBody = JSON.parse(textResponse);
            } catch (e) {
              responseBody = textResponse;
            }
          } catch (e) {
            console.error('Error reading response body:', e);
            responseBody = 'Could not read response';
          }
          
          if (response.ok) {
            console.log('SheetDB success:', responseBody);
          } else {
            console.error('SheetDB error response:', responseBody);
            // Still throw to trigger the catch
            throw new Error(`SheetDB error status: ${response.status}`);
          }
          return responseBody;
        })
        .catch(error => {
          console.error('SheetDB error details:', {
            message: error.message,
            name: error.name,
            stack: error.stack,
            toString: error.toString()
          });
          
          // Log direct API endpoint for manual testing
          console.log('You can test this API endpoint manually with:');
          console.log(`curl -X POST "${SHEETDB_ENDPOINT}" -H "Content-Type: application/json" -d '{"data":[${JSON.stringify(sheetPayload)}]}'`);
        });
      } catch (sheetDbErr) {
        console.error('Error with SheetDB attempt:', sheetDbErr);
        // Continue anyway since we saved to localStorage
      }
      
      // Always treat as success
      setSubmitStatus('success');
      setContactInfo('');
      
      // Close modal after delay
      setTimeout(() => {
        setShowEmailModal(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tokenDefinitions = {
    A: "Stablecoin pegged to USD. Under normal conditions, A is at least 110% collateralized. Under distressed conditions, A holders have rights to claim all B and B+ holders' backings till A holders are made whole. A can be redeemed at any time.",
    stA: "Shares all of A's value stability, overcollateralization, and any-time redemption guarantees. It is the yield-bearing, rebasing, staked version of A.",
    'B+': "Floating value, yield-accrual token. It's the second layer of buffer capital and offers higher yield than stA. B+, stA, and A are at least 102% collateralized. B+ redemption will be paused when A and stA are collateralized by <110%.",
    B: "Floating value, yield-accrual token. It's the first layer of buffer capital and offers the highest yield. B redemption will be paused when A, stA, and B+ are collateralized by <102%.",
    Bond: "Represents a delta-neutral position consisting of long spot and short dated future. Bond has a maturity date and reaches $1 at maturity, backed by redeemable liquid stables.",
    'Liquid Stables': "Refers to DAI, USDS, USDT, and USDC.",
    Cash: "Cash comprises three tiers: A / stA, B+, and B. Each tier is backed by designated pool of Bond and Liquid Stables. Cash can be minted from or redeemed for equal value Bond or Liquid Stables."
  };

  // Helper function to render a tooltip definition display
  const renderTooltip = (token) => {
    return (
      <div className="tooltip-container">
        <div className="tooltip">
          <div className="tooltip-header">{token}:</div>
          {tokenDefinitions[token]}
        </div>
      </div>
    );
  };

  return (
    <div className="app-container dark-mode" onClick={handleClickOutside}>
      <header className={`header ${expandedSection ? 'header-visible' : ''}`}>
        <div className="logo">
          <img className="logo-left" alt="logo-left-star" src={ leftstar }/>
          <span className="logo-super">Super</span>
          <span className="logo-space"></span>
          <span className="logo-italic">nova</span>
          <span className="logo-star">★</span>
        </div>
        <nav className="nav">
          <Link to="/" className="back-link">About</Link>
          <a href="https://supernova-10.gitbook.io/supernova/" target="_blank" rel="noopener noreferrer">Docs</a>
          <a href="#updates" onClick={handleUpdatesClick}>Updates</a>
          <a href="https://x.com/SupernovaXHQ" target="_blank" rel="noopener noreferrer">Contact</a>
        </nav>
      </header>

      {showEmailModal && (
        <div className="modal-overlay">
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <h3>Stay Updated</h3>
              <button className="modal-close" onClick={() => setShowEmailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {submitStatus === 'success' ? (
                <div className="success-message">
                  <span className="success-icon">✓</span>
                  <p>Thank you! We'll send you updates soon.</p>
                </div>
              ) : submitStatus === 'error' ? (
                <div className="success-message">
                  <div className="success-icon">⚠️</div>
                  <p>Something went wrong. Please try again.</p>
                  <button 
                    className="submit-button" 
                    onClick={() => setSubmitStatus(null)}
                    style={{ marginTop: '1rem' }}
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit}>
                  <p className="modal-description">Get the latest updates on our protocol development and launches.</p>
                  
                  <div className="contact-method-toggle">
                    <button 
                      type="button"
                      className={`toggle-button ${contactMethod === 'email' ? 'active' : ''}`}
                      onClick={() => handleContactMethodChange('email')}
                    >
                      Email
                    </button>
                    <button 
                      type="button"
                      className={`toggle-button ${contactMethod === 'telegram' ? 'active' : ''}`}
                      onClick={() => handleContactMethodChange('telegram')}
                    >
                      Telegram
                    </button>
                  </div>
                  
                  <div className="email-input-container">
                    <input
                      type={contactMethod === 'email' ? 'email' : 'text'}
                      value={contactInfo}
                      onChange={(e) => setContactInfo(e.target.value)}
                      placeholder={contactMethod === 'email' ? 'Enter your email' : 'Enter your Telegram handle (without @)'}
                      required
                      className="email-input"
                    />
                    <button 
                      type="submit" 
                      className="submit-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="container">
        <div className={`tagline ${expandedSection ? 'section-collapsed' : ''}`}>
          <p>When money moves fast and frictionless, the line between <span 
            className="clickable-keyword"
            onClick={() => toggleSection('bond')}
          >bond</span> and <span 
            className="clickable-keyword"
            onClick={() => toggleSection('cash')}
          >cash</span> begins to blur.</p>
          <p>Supernova builds protocols to accelerate this transition — supercharging on-chain capital efficiency.</p>
        </div>
        
        <div className={`section ${expandedSection === 'bond' ? '' : expandedSection ? 'section-collapsed' : ''}`} id="bond" ref={bondRef}>
          <h2 
            className={expandedSection === 'bond' ? 'expanded' : ''} 
            onClick={() => toggleSection('bond')}
          >
            Bond Protocol<span className="logo-star">★</span><span className="arrow">›</span>
          </h2>
          
          <div className={`section-content ${expandedSection === 'bond' ? 'expanded' : ''}`}>
            <div className="content-block" id="bond-block">
              <p>
                <span className="code section-title">Bond</span> represents a delta-neutral position combining a long spot and short dated future. Its value is market-determined pre-maturity, reaching $1 at maturity and backed by redeemable <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('Liquid Stables', 'bond-block');}}
                >Liquid Stables</span> post-maturity.
              </p>
              {activeTooltip === 'Liquid Stables' && activeBlockId === 'bond-block' && renderTooltip('Liquid Stables')}
              <p>
                When minted, <span className="code section-title">Bond</span> locks in the dated future's net premium as yield, realizing it at maturity — functioning like a fixed-yield debt instrument.
              </p>
            </div>
            <div className="content-block">
              <p><span className="code section-title">Bond</span> can be used as collateral to mint <span 
              className="code interactive" 
              onClick={(e) => {e.stopPropagation(); toggleTooltip('Cash', 'bond-block');}}>Cash</span>.</p>
              {activeTooltip === 'Cash' && activeBlockId === 'bond-block' && renderTooltip('Cash')}
              <a 
                href="https://app.supernova.vision" 
                className="launch-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Launch App<span className="arrow">›</span>
              </a>
            </div>
          </div>
        </div>
        
        <div className={`section ${expandedSection === 'cash' ? '' : expandedSection ? 'section-collapsed' : ''}`} id="cash" ref={cashRef}>
          <h2 
            className={expandedSection === 'cash' ? 'expanded' : ''} 
            onClick={() => toggleSection('cash')}
          >
            Cash Protocol<span className="logo-star">★</span><span className="arrow">›</span>
          </h2>
          
          <div className={`section-content ${expandedSection === 'cash' ? 'expanded' : ''}`}>
            <div className="content-block" id="cash-block-1">
              <p>
                <span className="code section-title">Cash</span> comprises three tiers: <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('A', 'cash-block-1');}}
                >A</span> / <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('stA', 'cash-block-1');}}
                >stA</span>, <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('B+', 'cash-block-1');}}
                >B+</span>, and <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('B', 'cash-block-1');}}
                >B</span>. Each tier is backed by designated pool of <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('Bond', 'cash-block-1');}}
                >Bond</span> and <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('Liquid Stables', 'cash-block-1');}}
                >Liquid Stables</span>.
              </p>
              {activeTooltip && activeBlockId === 'cash-block-1' && renderTooltip(activeTooltip)}
            </div>
            
            <div className="content-block" id="cash-block-2">
              <p>
                <span className="code section-title">Cash</span> can be minted from or redeemed for equal value <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('Bond', 'cash-block-2');}}
                >Bond</span> or <span 
                  className="code interactive" 
                  onClick={(e) => {e.stopPropagation(); toggleTooltip('Liquid Stables', 'cash-block-2');}}
                >Liquid Stables</span>.
              </p>
              {activeTooltip && activeBlockId === 'cash-block-2' && renderTooltip(activeTooltip)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Details; 